import React, { useEffect } from "react";
import { Text, Box, Button, Grid } from "@chakra-ui/react";
import { useMenu, type UseMenuProps } from "react-instantsearch";
import { useAlgoliaSearchParameters } from "./utils";

type AlgoliaSearchFiltersProps = {
  query: string;
  onFilterChanged?: () => void;
} & UseMenuProps;

export function AlgoliaSearchFilters({
  attribute,
  query,
  onFilterChanged,
  ...useMenuProps
}: AlgoliaSearchFiltersProps) {
  const { getSearchParameter, setSearchParameter } = useAlgoliaSearchParameters(attribute);
  const { items, refine } = useMenu({
    attribute,
    limit: 30,
    sortBy: ["name:asc"],
    ...useMenuProps,
  });

  useEffect(() => {
    const currentFilterValue = getSearchParameter();
    if (currentFilterValue) {
      const menuItem = items.find((item) => item.value === currentFilterValue);
      // Don't refine if filter is already selected, or it will be applied twice
      if (menuItem && !menuItem.isRefined) {
        refine(currentFilterValue);
      }
    }
  }, [attribute, getSearchParameter, items, refine]);

  return (
    <Grid as="ul" gap="smallest" mb="bigger" listStyleType="none" px="tiniest">
      {items.map((filter) => {
        return (
          <Box key={filter.label} as="li">
            <Button
              variant="unstyled"
              fontWeight="semibold"
              position="relative"
              textDecoration={filter.isRefined ? "underline" : "none"}
              textUnderlineOffset={3}
              color={filter.isRefined ? "new.green.700" : "brand.textSecondary.light"}
              fontSize="brand.sm"
              _hover={{ color: "brand.text.light" }}
              onClick={async () => {
                // Don't refine if filter is already selected
                if (!filter.isRefined) {
                  onFilterChanged?.();
                  refine(filter.value);
                  await setSearchParameter(filter.value);
                }
              }}
            >
              {filter.label}
              {query !== "" && (
                <Text as="span" ml={1}>
                  ({filter.count})
                </Text>
              )}
            </Button>
          </Box>
        );
      })}
    </Grid>
  );
}
